// src/components/reports/CompanyReport.jsx
import React from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
    Chart as ChartJS, ArcElement, Tooltip, Legend,
    CategoryScale, LinearScale, BarElement
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const CATEGORY_LABELS = {
    MATERIALS: 'Materiali', EQUIPMENT: 'Attrezzature', LABOR: 'Manodopera',
    FUEL: 'Carburante', SUBCONTRACT: 'Subappalti', TRANSPORT: 'Trasporti',
    PERMITS: 'Permessi', ADMIN: 'Amministrative', OTHER: 'Altro'
};

const STATUS_LABELS = {
    DRAFT: 'Bozza', PLANNED: 'Pianificato', IN_PROGRESS: 'In Corso',
    ON_HOLD: 'In Attesa', DELAYED: 'Ritardato', COMPLETED: 'Completato', CANCELLED: 'Cancellato'
};

const STATUS_COLORS = {
    DRAFT: 'bg-gray-100 text-gray-700', PLANNED: 'bg-blue-100 text-blue-700',
    IN_PROGRESS: 'bg-emerald-100 text-emerald-700', ON_HOLD: 'bg-yellow-100 text-yellow-700',
    DELAYED: 'bg-orange-100 text-orange-700', COMPLETED: 'bg-purple-100 text-purple-700',
    CANCELLED: 'bg-red-100 text-red-700'
};

const STATUS_BAR_COLORS = {
    DRAFT: '#94A3B8', PLANNED: '#3B82F6', IN_PROGRESS: '#10B981',
    ON_HOLD: '#F59E0B', DELAYED: '#F97316', COMPLETED: '#8B5CF6', CANCELLED: '#EF4444'
};

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6B7280', '#9CA3AF'];

const CompanyReport = ({ data }) => {
    if (!data || !data.summary) {
        return (
            <div className="bg-white rounded-2xl border p-8 text-center text-gray-400">
                Nessun dato disponibile
            </div>
        );
    }

    const summary = data.summary;
    const projects = data.projects || data.details?.projects || [];
    const expensesByCategory = data.details?.expensesByCategory || [];
    const projectsByStatus = data.details?.projectsByStatus || summary.projectStatusCount || [];

    // Doughnut spese per categoria
    const doughnutData = {
        labels: expensesByCategory.map(c => CATEGORY_LABELS[c.category] || c.category),
        datasets: [{
            data: expensesByCategory.map(c => c._sum?.totalAmount || 0),
            backgroundColor: CHART_COLORS,
            borderWidth: 0,
            hoverOffset: 8,
        }]
    };

    // Bar stati progetti
    const barData = {
        labels: projectsByStatus.map(s => STATUS_LABELS[s.status] || s.status),
        datasets: [{
            label: 'Progetti',
            data: projectsByStatus.map(s => s._count || 0),
            backgroundColor: projectsByStatus.map(s => STATUS_BAR_COLORS[s.status] || '#94A3B8'),
            borderRadius: 6,
        }]
    };

    return (
        <div className="space-y-6 animate-fade-in">

            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
                <p className="text-indigo-200 text-sm font-medium uppercase tracking-wider">Report Aziendale</p>
                <h2 className="text-2xl font-bold mt-1">Panoramica Organizzazione</h2>
                {data.dateRange && (
                    <p className="text-indigo-200 text-sm mt-2">
                        📅 {formatDate(data.dateRange.from)} → {formatDate(data.dateRange.to)}
                    </p>
                )}
            </div>

            {/* KPI principali */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Progetti', value: summary.totalProjects, icon: '🏗️', color: 'blue' },
                    { label: 'Spese Totali', value: formatCurrency(summary.totalExpenses), icon: '💰', color: 'emerald' },
                    { label: 'Utenti', value: summary.totalUsers, icon: '👥', color: 'purple' },
                    { label: 'Task', value: summary.totalTasks, icon: '✅', color: 'orange' },
                ].map((kpi) => (
                    <div key={kpi.label} className={`bg-${kpi.color}-50 border border-${kpi.color}-100 rounded-2xl p-5`}>
                        <div className="flex items-center justify-between">
                            <p className={`text-xs font-semibold text-${kpi.color}-500 uppercase tracking-wider`}>{kpi.label}</p>
                            <span className="text-xl">{kpi.icon}</span>
                        </div>
                        <p className={`text-2xl font-bold text-${kpi.color}-700 mt-2`}>{kpi.value}</p>
                    </div>
                ))}
            </div>

            {/* Grafici */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {expensesByCategory.length > 0 && (
                    <div className="bg-white rounded-2xl border p-5">
                        <h3 className="font-semibold text-gray-900 mb-4">Spese per Categoria</h3>
                        <div className="h-60">
                            <Doughnut data={doughnutData} options={{
                                maintainAspectRatio: false,
                                cutout: '65%',
                                plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 14, font: { size: 11 } } } }
                            }} />
                        </div>
                    </div>
                )}
                {projectsByStatus.length > 0 && (
                    <div className="bg-white rounded-2xl border p-5">
                        <h3 className="font-semibold text-gray-900 mb-4">Progetti per Stato</h3>
                        <div className="h-60">
                            <Bar data={barData} options={{
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false } },
                                scales: {
                                    y: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { color: '#F1F5F9' } },
                                    x: { grid: { display: false } }
                                }
                            }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Dettaglio spese per categoria */}
            {expensesByCategory.length > 0 && (
                <div className="bg-white rounded-2xl border overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900">Spese per Categoria</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                        {expensesByCategory.map((cat, idx) => {
                            const total = expensesByCategory.reduce((s, c) => s + (c._sum?.totalAmount || 0), 0);
                            const pct = total > 0 ? ((cat._sum?.totalAmount || 0) / total * 100).toFixed(1) : 0;
                            return (
                                <div key={cat.category} className="px-6 py-3 flex items-center gap-4">
                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                                    <span className="text-sm text-gray-700 flex-1">{CATEGORY_LABELS[cat.category] || cat.category}</span>
                                    <div className="flex items-center gap-3">
                                        <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden hidden sm:block">
                                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
                                        <span className="text-sm font-semibold text-gray-900 w-28 text-right">{formatCurrency(cat._sum?.totalAmount || 0)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Lista progetti */}
            {projects.length > 0 && (
                <div className="bg-white rounded-2xl border overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900">Lista Progetti ({projects.length})</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead>
                                <tr className="text-xs text-gray-400 uppercase tracking-wider">
                                    <th className="px-6 py-3 text-left">Progetto</th>
                                    <th className="px-6 py-3 text-left">Stato</th>
                                    <th className="px-6 py-3 text-right">Budget</th>
                                    <th className="px-6 py-3 text-right">Spese</th>
                                    <th className="px-6 py-3 text-right">Task</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {projects.map((project) => (
                                    <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-3">
                                            <p className="text-sm font-medium text-gray-900">{project.name}</p>
                                            <p className="text-xs text-gray-400">{project.code}</p>
                                        </td>
                                        <td className="px-6 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-700'}`}>
                                                {STATUS_LABELS[project.status] || project.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatCurrency(project.estimatedBudget)}</td>
                                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{project._count?.expenses || 0} spese</td>
                                        <td className="px-6 py-3 text-sm text-gray-900 text-right">{project._count?.tasks || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyReport;